import * as Api from '@ViewModels';
import moment from 'moment-timezone';
import * as React from 'react';
import { toast } from 'sonner';
import { useErrorMessages, useFullscreenModal, useUserSession } from '../../../models/hooks/appStateHooks';
import { useEventLogging } from '../../../models/Logging';
import {
	invalidateSchedulerConfigsQuery,
	useSchedulerConfigAddMutation,
	useSchedulerConfigCloneMutation,
	useSchedulerConfigDeleteMutation,
	useSchedulerConfigsQuery,
} from '../../../queries';
import { IMeetingConfigModalLocationState } from '../../components/settings/PersonalSettings/MeetingConfigModal';

export function useScheduleMeetingSettings({
	user,
	onEditMeetingClicked: _onEditMeetingClicked,
}: {
	user: Api.IUser;
	onEditMeetingClicked?: (meeting: Api.MeetingConfigViewModel) => void;
}) {
	const userSession = useUserSession();
	const logger = useEventLogging();
	const errorMessages = useErrorMessages();
	const fullscreenModal = useFullscreenModal();

	const schedulerConfigQuery = useSchedulerConfigsQuery({ userId: user.id });
	const addMeetingMutation = useSchedulerConfigAddMutation({
		onError: err => {
			logger.logApiError('ScheduleMeetingCreateNew-Error', err);

			errorMessages.pushApiError(err);
		},
		onSuccess: meetingResponse => {
			invalidateSchedulerConfigsQuery({ userId: user.id });
			editMeeting(new Api.MeetingConfigViewModel(userSession, meetingResponse, user));
		},
	});

	const deleteMeetingMutation = useSchedulerConfigDeleteMutation({
		onError: err => {
			logger.logApiError('ScheduleMeetingDelete-Error', err);

			errorMessages.pushApiError(err);
		},
		onSuccess: () => {
			invalidateSchedulerConfigsQuery({ userId: user.id });

			toast.success('Meeting deleted successfully.');
		},
	});
	const meetingConfigs = React.useMemo(() => {
		if (schedulerConfigQuery.data) {
			return schedulerConfigQuery.data.values.map(config => new Api.MeetingConfigViewModel(userSession, config, user));
		}
		return [];
	}, [schedulerConfigQuery.data, user, userSession]);

	const editMeeting = (meeting: Api.MeetingConfigViewModel) => {
		const locationState: IMeetingConfigModalLocationState = {
			viewModel: meeting,
		};

		fullscreenModal.history.push({
			pathname: '/meeting/config',
			state: locationState,
		});
	};

	const cloneMeetingMutation = useSchedulerConfigCloneMutation({
		onError: err => {
			logger.logApiError('ScheduleMeetingClone-Error', err);
			errorMessages.pushApiError(err);
		},
		onSuccess: meetingResponse => {
			invalidateSchedulerConfigsQuery({ userId: user.id });
			const newMeeting = new Api.MeetingConfigViewModel(userSession, meetingResponse, user);
			editMeeting(newMeeting);
		},
	});

	const onCloneMeeting = (sourceMeeting: Api.MeetingConfigViewModel) => {
		const timeZone = userSession.user.userPreferences?.timeZone || moment.tz.guess();
		cloneMeetingMutation.mutate({
			sourceMeetingId: sourceMeeting.id,
			userId: user.id,
			timeZone,
		});
	};

	return {
		isLoadingMeetingLinks: schedulerConfigQuery.isPending,
		meetingConfigs,

		onAddMeeting: () => addMeetingMutation.mutate({ userId: user.id }),
		onDeleteMeeting: (meeting: Api.MeetingConfigViewModel) =>
			deleteMeetingMutation.mutate({ meetingId: meeting.id, userId: user.id }),
		onEditMeeting: editMeeting,
		onCloneMeeting,
	};
}
